import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, Data } from '@angular/router';
import { SelectQuotePaymentOptionsFacade } from '@common/data-access-quotes';
import { ResetStateFacade } from '@common/data-access-shared';
import { UserProfileFacade } from '@common/data-access-user-profile';
import { fadeInOut } from '@common/ui-sales-components';
import {
  BuildConfigService,
  LoaderService,
  SessionInactivityService,
  MetaDataService,
} from '@common/util-foundation';
import { BuildConfig } from '@common/util-models';
import { AuthLibFacade } from '@domgen/dgx-fe-auth';
import { AlertDialogService } from '@domgen/dgx-fe-components-core';
import { NavigationLinks } from '@domgen/dgx-fe-business-models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthStateService } from '@shared/feature-auth';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, mapTo, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'hoover-app-root',
  templateUrl: './hoover-app.component.html',
  styleUrls: ['./hoover-app.component.scss'],
  animations: [fadeInOut],
})
export class HooverAppComponent implements OnInit {
  title = 'hoover app';
  config: BuildConfig = this.buildConfigService.config;
  headerNavigationLinks: NavigationLinks = this.config.header;
  footerNavigationLinks: BuildConfig['footer'] = this.config.footer;
  loading$!: Observable<boolean>;
  isCheckoutPage = false;

  public progressSteps$:
    | Observable<{ label: string; routes: string[] }[]>
    | undefined;
  public currentRoute = '';

  constructor(
    public readonly buildConfigService: BuildConfigService,
    public readonly userProfileFacade: UserProfileFacade,
    private readonly authFacade: AuthLibFacade,
    private readonly authStateService: AuthStateService,
    private readonly alertDialogService: AlertDialogService,
    private readonly loaderService: LoaderService,
    private readonly resetStateFacade: ResetStateFacade,
    private readonly router: Router,
    private readonly selectQuotePaymentOptionsFacade: SelectQuotePaymentOptionsFacade,
    private readonly sessionInactivityService: SessionInactivityService,
    private readonly metaDataService: MetaDataService,
    private readonly activatedRoute: ActivatedRoute
  ) {
    router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        if (e.url) {
          this.currentRoute = e.url;
          this.isCheckoutPage =
            this.buildConfigService.config.checkoutLoggedInUserPage ===
            this.currentRoute;
        }
      });

    this.progressSteps$ = combineLatest([
      this.selectQuotePaymentOptionsFacade.paymentOptions$,
      this.authFacade.isAuthenticated$,
    ]).pipe(
      map(([paymentOptions, isAuthenticated]) => {
        // In case there are more then one payment method then it should be boiler (heating equipment)
        return this.getSteps(
          isAuthenticated,
          paymentOptions.card !== undefined &&
            paymentOptions.directDebit !== undefined
        );
      })
    );
  }

  ngOnInit(): void {
    this.loading$ = this.loaderService.loading$;
    this.authStateService.setUpdateAuthState();
    this.monitorSessionInactivity();
    this.setMetaData();
  }

  private getSteps(
    isAuthenticated: boolean,
    isHeating: boolean
  ): { label: string; routes: string[] }[] {
    const yourDetails = {
      label: 'Your Details',
      routes: ['/checkout/personal-details'],
    };
    const paymentMethod = {
      label: 'Payment Method',
      routes: ['/checkout/select-payment'],
    };
    const paymentDetails = {
      label: 'Payment Details',
      routes: [
        '/checkout/direct-debit-payment',
        '/checkout/card-payment',
        '/checkout/credit-card-payment',
        '/checkout/logged-in',
      ],
    };
    const confirmation = {
      label: 'Confirmation',
      routes: ['/checkout/order-confirmation'],
    };
    return [
      ...(!isAuthenticated ? [yourDetails] : []),
      ...(isHeating ? [paymentMethod] : []),
      paymentDetails,
      confirmation,
    ];
  }

  private monitorSessionInactivity(): void {
    this.sessionInactivityService
      .monitor()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.alertDialogService.openError({
          ariaLabel: 'session expired',
          disableClose: true,
          data: {
            title: 'Your session has timed out due to inactivity',
            body: 'Click below to restart the journey',
            primaryCta: {
              text: 'Restart journey',
              click: () => this.resetStateFacade.sessionInactivityTimeout(),
            },
          },
        });
      });
  }

  private setMetaData(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        mapTo(this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this)
      )
      .subscribe((data: Data) => {
        this.metaDataService.setTitle(data['title']);
        this.metaDataService.setDescription(
          data['title'] + data['description']
        );
      });
  }
}
